import { useMemo } from 'react';

import { KeyboardArrowLeft as KeyboardArrowLeftIcon } from '@mui/icons-material';
import { Button, ButtonGroup, ButtonProps, Stack, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { UserRoleEnum } from '~enums';
import { useAuthorize } from '~features/authentication';
import { OrganisationAutocomplete, useUrlSearchParamsOrganisation } from '~features/organisations';

import pageHeaderButtonsAtom from '../atoms/pageHeaderButtonsAtom';
import pageTitleAtom from '../atoms/pageTitleAtom';

interface Page {
	/**
	 * The translation key
	 */
	i18nKey: string;
	path: string;
	// Otherwise defaults to admin?
	roles?: UserRoleEnum[];
}

export interface PageHeaderProps {
	/**
	 * Use a translation key to show a title. Notice that setting
	 * the title through the hook takes precedence
	 */
	i18nTitleKey?: string;
	/**
	 * Set an untranslated title. Notice that the `i18nTitleKey` takes
	 * precedence
	 */
	title?: string;
	enableOrganisationSelect?: boolean;
	buttons?: ButtonProps[];
}

const PageHeader = ({ enableOrganisationSelect = false, buttons, ...props }: PageHeaderProps) => {
	const { t } = useTranslation();

	const location = useLocation();
	const navigate = useNavigate();
	const { isSuperAdmin } = useAuthorize();

	const pageTitle = useAtomValue(pageTitleAtom);
	const pageHeaderButtons = useAtomValue(pageHeaderButtonsAtom);
	const [organisation, setOrganisation] = useUrlSearchParamsOrganisation();

	const pathDepth = location.pathname.split('/').length - 1;
	const headerButtons = useMemo(
		() => pageHeaderButtons ?? buttons ?? [],
		[pageHeaderButtons, buttons]
	);

	const handleGoBackClick = () => {
		if (location.state?.from != null) {
			navigate(location.state?.from);
		} else {
			navigate(-1);
		}
	};

	const title = pageTitle || (props.i18nTitleKey != null ? t(props.i18nTitleKey) : props.title);

	return (
		<Stack width={1} spacing={1} alignItems='flex-start'>
			{(pathDepth > 2 || location.state?.from != null) && (
				<Button startIcon={<KeyboardArrowLeftIcon />} onClick={handleGoBackClick}>
					{location.state?.label ?? t('ui.button.contained.back')}
				</Button>
			)}
			<Stack
				width={1}
				spacing={1}
				direction='row'
				justifyContent='space-between'
				alignItems='center'
			>
				<Stack direction='row' spacing={3}>
					<Typography variant='h3' py={1}>
						{title}
					</Typography>

					{enableOrganisationSelect && isSuperAdmin() && (
						<OrganisationAutocomplete
							value={organisation}
							onChange={(_, newValue) => setOrganisation(newValue)}
							sx={{ width: 250 }}
						/>
					)}
				</Stack>

				<Stack direction='row' gap={1}>
					{headerButtons.length > 0 && (
						<ButtonGroup
							variant='text'
							sx={{
								// '.MuiButtonGroup-grouped:not(:last-of-type)': {
								// 	borderColor: 'transparent',
								// },
								gap: 1,
								'.MuiButtonGroup-grouped': {
									borderColor: 'transparent',
									borderRadius: 26,
								},
							}}
						>
							{headerButtons.map((el, i) => (
								<Button key={i} {...el} />
							))}
						</ButtonGroup>
					)}
				</Stack>
			</Stack>
		</Stack>
	);
};

export type { Page };

export default PageHeader;
